import React from 'react';
import { CORES_GRAFICO } from '../../utils/graficos';

/**
 * Gráfico de barras horizontais em SVG puro.
 *
 * Props:
 *  - data: [{ label, value, color?, secondValue?, secondColor? }]
 *  - formatValue: (v) => string (formata o valor exibido)
 *  - formatSecond: (v) => string (valor da segunda barra; fallback formatValue)
 *  - height: altura por barra em px (padrão 28)
 *  - showSecond: se true, mostra secondValue como barra sobreposta (comparativo)
 *  - legenda: [{ cor, texto }] opcional
 *  - labelWidth: largura da coluna de rótulos (padrão 120)
 */

export default function BarChart({
  data,
  formatValue,
  formatSecond,
  height = 28,
  showSecond = false,
  legenda,
  labelWidth = 120,
}) {
  if (!data || data.length === 0) return null;

  const valores = data.flatMap(d => [d.value || 0, showSecond ? (d.secondValue || 0) : 0]);
  const maxVal = Math.max(...valores, 1);
  const gap = 8;
  const labelW = labelWidth;
  const valueW = showSecond ? 100 : 80;
  const barAreaW = 200;
  const totalW = labelW + barAreaW + valueW + 20;
  const totalH = data.length * (height + gap) + gap;
  const fmt1 = formatValue || ((v) => String(v ?? ''));
  const fmt2 = formatSecond || fmt1;

  return (
    <div>
      {legenda?.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.75rem',
          marginBottom: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)',
        }}>
          {legenda.map((l) => (
            <span key={l.texto} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                width: 10, height: 10, borderRadius: 2, background: l.cor, display: 'inline-block',
              }} />
              {l.texto}
            </span>
          ))}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <svg width="100%" viewBox={`0 0 ${totalW} ${totalH}`} style={{ display: 'block', maxWidth: totalW }}>
          {data.map((item, i) => {
            const y = gap + i * (height + gap);
            const barW = maxVal > 0 ? (item.value / maxVal) * barAreaW : 0;
            const barW2 = showSecond && item.secondValue ? (item.secondValue / maxVal) * barAreaW : 0;
            const cor = item.color || CORES_GRAFICO[0];
            const cor2 = item.secondColor || CORES_GRAFICO[3];
            const rotulo = item.label.length > 18 ? item.label.slice(0, 17) + '…' : item.label;

            return (
              <g key={i}>
                <text
                  x={labelW - 8}
                  y={y + height / 2 + 1}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize="11"
                  fill="var(--text-muted)"
                >
                  {rotulo}
                </text>

                <rect
                  x={labelW}
                  y={y + 2}
                  width={barAreaW}
                  height={height - 4}
                  rx={4}
                  fill="var(--border)"
                  opacity={0.5}
                />

                <rect
                  x={labelW}
                  y={y + 2}
                  width={barW}
                  height={showSecond ? (height - 4) / 2 : height - 4}
                  rx={4}
                  fill={cor}
                  opacity={0.85}
                  style={{ transition: 'width 0.4s ease' }}
                />

                {showSecond && barW2 > 0 && (
                  <rect
                    x={labelW}
                    y={y + 2 + (height - 4) / 2}
                    width={barW2}
                    height={(height - 4) / 2}
                    rx={4}
                    fill={cor2}
                    opacity={0.85}
                    style={{ transition: 'width 0.4s ease' }}
                  />
                )}

                <text
                  x={labelW + barAreaW + 8}
                  y={y + height / 2 + 1}
                  dominantBaseline="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="var(--text)"
                >
                  {showSecond
                    ? `${fmt1(item.value)} / ${fmt2(item.secondValue)}`
                    : fmt1(item.value)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
